import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTSVG} from '../../../../../_gori/helpers'
import {SERVICE_INFO} from '../../../../../_gori/constants'
import {getCarrierService, getDeliveryWindowValues} from './rateHelpers'

type PresetRateSectionProps = {
  presetService: string
  presetCarrierName: string
  carriers: any
  rateOptions: any[]
  isInternational: boolean
  loadingCarriers: string[]
  retryingCarriers: string[]
  fetchedCarriers: Set<string>
  renderTimeoutCard: (item: any) => React.ReactNode
  renderItemService: (item: any, className?: string, index?: number) => React.ReactNode
}

/**
 * Shared empty card for preset states where no priced rate is available.
 * Used by both "not applicable for route" and "fetched with no rates" states.
 */
const PresetEmptyCard: React.FC<{
  logo?: string
  displayName: string
  carrierName: string
  serviceInfo: any
  subText?: string
}> = ({logo, displayName, carrierName, serviceInfo, subText}) => {
  const intl = useIntl()

  return (
    <div className='btn border border-secondary d-flex justify-content-between my-5 text-start pe-none opacity-75'>
      <div className='d-flex align-items-center flex-grow-1'>
        <div className='symbol symbol-40px me-5'>
          <span className='symbol-label bg-transparent'>
            {logo && <img src={logo} className='h-100 align-self-center' alt={carrierName} />}
          </span>
        </div>
        <div className='flex-grow-1'>
          <div className='d-flex align-items-left mb-1'>
            <span className='service-name text-capitalize text-start me-2'>{displayName}</span>
          </div>
          {serviceInfo && (
            <div className='text-start'>
              <div className='service-info-description fw-normal mb-1'>
                {intl.formatMessage({id: serviceInfo.description})}
              </div>
              <div className='service-info-delivery-window text-capitalize'>
                {intl.formatMessage(
                  {id: serviceInfo.deliveryWindow},
                  getDeliveryWindowValues(serviceInfo)
                )}
              </div>
            </div>
          )}
          {subText && <div className='fs-8 text-gray-500'>{subText}</div>}
        </div>
      </div>
    </div>
  )
}

/**
 * Renders the Preset section when the preset service does NOT have a priced rate.
 * Handles multiple states: not applicable for route, retrying, timeout, error,
 * fetched with no rate (selectable), and not yet fetched (loading).
 */
export const PresetRateSection: React.FC<PresetRateSectionProps> = ({
  presetService,
  presetCarrierName,
  carriers,
  rateOptions,
  isInternational,
  loadingCarriers,
  retryingCarriers,
  fetchedCarriers,
  renderTimeoutCard,
  renderItemService,
}) => {
  const intl = useIntl()

  const carrierService = getCarrierService(carriers, presetCarrierName)
  const logo = carrierService?.visuals?.logo
  const displayName = carrierService?.carrier_label || presetCarrierName.toUpperCase()
  const isLoading = loadingCarriers.includes(presetCarrierName)
  const isRetrying = retryingCarriers.includes(presetCarrierName)
  const alreadyFetched = fetchedCarriers.has(presetCarrierName)
  const fetchedButNoRate = alreadyFetched && !isLoading

  const errorRate = useMemo(
    () =>
      rateOptions.find(
        (item) => item?.carrier_service === `${presetCarrierName}_error` && item?.error
      ),
    [rateOptions, presetCarrierName]
  )

  // Check if the preset SERVICE is applicable for the current route type.
  const presetServiceConfig = carriers?.[presetService]
  const isServiceApplicable =
    presetServiceConfig?.international === undefined ||
    presetServiceConfig?.international === isInternational

  const presetServiceInfo = SERVICE_INFO[presetService] as any
  const presetVisuals = carriers?.[presetService]?.visuals
  const presetLogo = presetVisuals?.logo || logo
  const presetDisplayName = presetVisuals?.display_name || displayName

  // --- Not applicable for route ---
  if (!isServiceApplicable) {
    return (
      <div className='mb-5'>
        <span className='fw-bolder fw-bold fs-7'>{intl.formatMessage({id: 'PRESET'})}</span>
        <PresetEmptyCard
          logo={presetLogo}
          displayName={presetDisplayName}
          carrierName={presetCarrierName}
          serviceInfo={presetServiceInfo}
        />
        <div className='d-flex align-items-start gap-2 px-2 mt-n3 mb-4'>
          <KTSVG
            path='/media/gori/settings/errorsPreset.svg'
            className='svg-icon-4 svg-icon-danger mt-1'
          />
          <span className='text-danger fs-7'>
            {intl.formatMessage({id: 'PRESET_NOT_APPLICABLE_FOR_ROUTE_1'})}{' '}
            {intl.formatMessage(
              {id: 'PRESET_NOT_APPLICABLE_FOR_ROUTE_2'},
              {allRates: <span className='fw-bold'>{intl.formatMessage({id: 'ALL_RATES'})}</span>}
            )}
          </span>
        </div>
      </div>
    )
  }

  // --- Currently retrying ---
  if (isRetrying && errorRate) {
    return (
      <div className='mb-5'>
        <span className='fw-bolder fw-bold fs-7'>{intl.formatMessage({id: 'PRESET'})}</span>
        {renderTimeoutCard(errorRate)}
      </div>
    )
  }

  // --- Fetched with a timeout ---
  if (fetchedButNoRate && errorRate?.isTimeout) {
    return (
      <div className='mb-5'>
        <span className='fw-bolder fw-bold fs-7'>{intl.formatMessage({id: 'PRESET'})}</span>
        {renderTimeoutCard(errorRate)}
      </div>
    )
  }

  // --- Fetched with a non-timeout error ---
  if (fetchedButNoRate && errorRate) {
    return (
      <div className='mb-5'>
        <span className='fw-bolder fw-bold fs-7'>{intl.formatMessage({id: 'PRESET'})}</span>
        {renderItemService(errorRate, '', 0)}
      </div>
    )
  }

  // --- Fetched with no rates — show empty carrier card ---
  if (fetchedButNoRate) {
    return (
      <div className='mb-5'>
        <span className='fw-bolder fw-bold fs-7'>{intl.formatMessage({id: 'PRESET'})}</span>
        <PresetEmptyCard
          logo={presetLogo}
          displayName={presetDisplayName}
          carrierName={presetCarrierName}
          serviceInfo={presetServiceInfo}
          subText={intl.formatMessage({id: 'NO_RATES_AVAILABLE_CARRIER'})}
        />
      </div>
    )
  }

  // --- Not yet fetched — skeleton loading state ---
  return (
    <div className='mb-5'>
      <span className='fw-bolder fw-bold fs-7'>{intl.formatMessage({id: 'PRESET'})}</span>
      <div className='text-muted mt-4 fw-bold fs-7 mb-4 border border-secondary rounded border-dashed border-1 p-3 d-flex justify-content-between'>
        <div className='col-10'>
          <span className='btn col-1 placeholder placeholder-lg rounded-2 bg-secondary me-2' />
          <span className='fs-3 col-3 placeholder placeholder-lg rounded-2 bg-secondary me-2' />
        </div>
        <span className='btn col-1 placeholder placeholder-lg rounded-2 bg-secondary me-2' />
      </div>
    </div>
  )
}
